// Physics world: the static BSP plus solid brush entities, exposing the trace
// interface used by pmove (equivalent of the server's SV_Trace).

import { CollisionModel } from './cm/cm';
import type { TraceResult } from './cm/types';
import { f, flength } from './f32';
import type { Vec3 } from './math';
import { ENTITYNUM_NONE, ENTITYNUM_WORLD, type PmoveEnv } from './physics/pmove';

export interface SolidEntity {
  entityNum: number;
  model: number;
  origin: Vec3;
  /** Rotation (pitch yaw roll in degrees); all zero for most entities. */
  angles: Vec3;
  absmin: Vec3;
  absmax: Vec3;
}

/** RadiusFromBounds */
export function radiusFromBounds(mins: Vec3, maxs: Vec3): number {
  const corner: Vec3 = [0, 0, 0];
  for (let i = 0; i < 3; i++) corner[i] = Math.max(Math.abs(mins[i]), Math.abs(maxs[i]));
  return flength(corner);
}

export class PhysicsWorld implements PmoveEnv {
  solids: SolidEntity[] = [];
  private byEntity = new Map<number, SolidEntity>();

  constructor(readonly cm: CollisionModel) {}

  addSolid(entityNum: number, model: number, origin: Vec3, angles: Vec3 = [0, 0, 0]): SolidEntity | null {
    const m = this.cm.models[model];
    if (!m) return null;
    const s: SolidEntity = { entityNum, model, origin: [0, 0, 0], angles: [0, 0, 0], absmin: [0, 0, 0], absmax: [0, 0, 0] };
    this.solids.push(s);
    this.byEntity.set(entityNum, s);
    this.setPose(entityNum, origin, angles);
    return s;
  }

  solid(entityNum: number): SolidEntity | undefined {
    return this.byEntity.get(entityNum);
  }

  /** Moves a solid entity (SV_LinkEntity bounds: rotated models use their radius). */
  setPose(entityNum: number, origin: Vec3, angles: Vec3): void {
    const s = this.byEntity.get(entityNum);
    if (!s) return;
    const m = this.cm.models[s.model];
    for (let i = 0; i < 3; i++) {
      s.origin[i] = origin[i];
      s.angles[i] = angles[i];
    }
    if (angles[0] || angles[1] || angles[2]) {
      const r = radiusFromBounds(m.mins, m.maxs);
      for (let i = 0; i < 3; i++) {
        s.absmin[i] = f(f(origin[i] - r) - 1);
        s.absmax[i] = f(f(origin[i] + r) + 1);
      }
    } else {
      for (let i = 0; i < 3; i++) {
        s.absmin[i] = m.mins[i] + origin[i];
        s.absmax[i] = m.maxs[i] + origin[i];
      }
    }
  }

  trace(start: Vec3, mins: Vec3, maxs: Vec3, end: Vec3, mask: number): TraceResult {
    const tr = this.cm.boxTrace(start, end, mins, maxs, mask);
    // SV_Trace: only a hit (not a start in solid) counts as touching the world
    tr.entityNum = tr.fraction !== 1 ? ENTITYNUM_WORLD : ENTITYNUM_NONE;
    if (tr.allsolid || !this.solids.length) return tr;

    let result = tr;
    for (const s of this.solids) {
      // broad phase: bounds of the whole move against the entity
      let skip = false;
      for (let i = 0; i < 3; i++) {
        const lo = Math.min(start[i], end[i]) + mins[i];
        const hi = Math.max(start[i], end[i]) + maxs[i];
        if (lo > s.absmax[i] || hi < s.absmin[i]) {
          skip = true;
          break;
        }
      }
      if (skip) continue;
      const t = this.cm.boxTrace(start, end, mins, maxs, mask, s.model, s.origin, s.angles);
      if (t.allsolid) {
        result.allsolid = true;
        t.entityNum = s.entityNum;
      } else if (t.startsolid) {
        result.startsolid = true;
        t.entityNum = s.entityNum;
      }
      if (t.fraction < result.fraction) {
        // make sure we keep a startsolid from a previous trace
        const oldStart = result.startsolid;
        t.entityNum = s.entityNum;
        result = t;
        result.startsolid = result.startsolid || oldStart;
      }
    }
    return result;
  }

  pointContents(p: Vec3): number {
    let contents = this.cm.pointContents(p);
    for (const s of this.solids) {
      if (p[0] < s.absmin[0] || p[1] < s.absmin[1] || p[2] < s.absmin[2] || p[0] > s.absmax[0] || p[1] > s.absmax[1] || p[2] > s.absmax[2]) continue;
      contents |= this.cm.pointContents(p, s.model, s.origin, s.angles);
    }
    return contents;
  }

  /** Does a box at origin touch any brush of the inline model (trap_EntityContact)? */
  boxTouchesModel(origin: Vec3, mins: Vec3, maxs: Vec3, model: number, modelOrigin: Vec3, angles?: Vec3): boolean {
    const t = this.cm.boxTrace(origin, origin, mins, maxs, -1, model, modelOrigin, angles);
    return t.startsolid;
  }
}
